import {
  EditOutlined,
  LinkOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import {
  Button,
  Card,
  Descriptions,
  Form,
  Modal,
  Select,
  Switch,
  Tabs
} from 'antd'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '~/components/PageHeader'
import { LocalDateTime } from '~/components/LocalDateTime'
import { useUrlQuery } from '~/hooks/useUrlParams'
import { regionCodeToName } from '~/utils/constants'
import { useGetDoctors } from '../Courses/query'
import EmailTemplates from '../EmailTemplates'
import CourseParticipants from './CourseParticipants'
import CourseServers from './CourseServers'
import UploadedCases from './UploadedCases'
import UtilizationReport from './UtilizationReport'
import {
  useAssignCourseUser,
  useDeleteCourseMutation,
  useDuplicateCourseMutation,
  useUpdateCourseSettingMutation
} from './mutation'
import { useCourseQuery } from './query'
import RegistrationList from './RegistrationList'
import WaitlistList from './WaitlistList'

const { confirm } = Modal

const CourseDetails = () => {
  const { id } = useParams()
  const { data: course } = useCourseQuery(Number(id))
  const {
    mutate: updateCourseSetting,
    isLoading: updatingCourseSetting,
    variables: courseSetting
  } = useUpdateCourseSettingMutation(Number(id))
  const { mutate: duplicateCourse } = useDuplicateCourseMutation()
  const [openAssignUserModel, setOpenAssignUserModel] = useState(false)

  const { mutate: assignCourseUserMutate } = useAssignCourseUser(id)
  const assignUserForm = useRef()
  const handleAssignUser = useCallback(
    (values) => {
      assignCourseUserMutate(values, {
        onSuccess: () => {
          setOpenAssignUserModel(false)
        }
      })
    },
    [assignCourseUserMutate]
  )

  const { data } = useGetDoctors()

  const users = useMemo(() => {
    if (!course || !course.instructors) {
      return ''
    }

    return <div>{course.doctors.map((i) => i.name).join(', ')}</div>
  }, [course])

  const instructors = useMemo(() => {
    if (!course || !course.instructors) {
      return ''
    }

    return <div>{course.case_uploaders.map((i) => i.name).join(', ')}</div>
  }, [course])

  const casSupports = useMemo(() => {
    if (!course || !course.cas_supports) {
      return ''
    }

    return <div>{course.cas_supports.map((i) => i.name).join(', ')}</div>
  }, [course])

  const caseUploading = useMemo(() => {
    if (!course || !course.case_uploaders) {
      return ''
    }
    return <div>{course.case_uploaders.map((i) => i.name).join(', ')}</div>
  }, [course])

  const { mutate: deleteCourse } = useDeleteCourseMutation()
  const confirmDelete = (courseId, courseName) => {
    confirm({
      title: 'Confirm',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to delete course ${courseName}?`,
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        deleteCourse(courseId)
      }
    })
  }

  const { queryValue, setQueryValue } = useUrlQuery({
    name: 'tab',
    defaultValue: 'participants'
  })

  if (course == null) {
    return null
  }

  const items = [
    {
      key: 'participants',
      label: 'Workstations',
      children: <CourseParticipants />
    },
    {
      key: 'registrations',
      label: 'Registrations',
      children: <RegistrationList course={course} />
    },
    {
      key: 'waitlists',
      label: 'Waitlists',
      children: <WaitlistList course={course} />
    },
    {
      key: 'servers',
      label: 'Servers',
      children: (
        <CourseServers
          showSyncButton={course.sync_servers_enabled && !course.is_ended}
        />
      )
    },
    {
      key: 'uploaded-cases',
      label: 'Uploaded Cases',
      children: <UploadedCases />
    },
    {
      key: 'utilization-report',
      label: 'Utilization Report',
      children: <UtilizationReport />
    },
    {
      key: 'email-templates',
      label: 'Email Templates',
      children: <EmailTemplates course={course} />
    }
  ]

  return (
    <>
      <PageHeader
        className='radup-page-header'
        title={`# ${course?.id} - ${course?.name}`}
        onBack={() => window.history.back()}
        extra={[
          <Link key='edit' to={`/admin/courses/edit/${course.id}`}>
            <Button type='primary' icon={<EditOutlined />} />
          </Link>,
          <Button
            key='duplicate'
            type='primary'
            onClick={() => duplicateCourse(course?.id)}
          >
            Duplicate
          </Button>,
          <Button
            key='add-to-user'
            type='primary'
            onClick={() => {
              setOpenAssignUserModel(true)
            }}
          >
            Add to user
          </Button>,
          <Button
            key='delete'
            type='primary'
            onClick={() => confirmDelete(course?.id, course?.name)}
          >
            Delete
          </Button>,
          <Link
            key='registration_link'
            to={`/courses/${course.course_token}/register`}
            target='_blank'
          >
            <Button type='primary' icon={<LinkOutlined />}>
              Registration link
            </Button>
          </Link>
        ]}
      />
      <Card title='Event Info'>
        <Descriptions>
          <Descriptions.Item key='Software' label='Software'>
            {course?.software?.name}
          </Descriptions.Item>
          <Descriptions.Item key='Set-up location' label='Set-up location'>
            {regionCodeToName(course?.setup_location) || 'n/a'}
          </Descriptions.Item>
          <Descriptions.Item key='Upload location' label='Upload location'>
            {course.upload_location_name ?? 'n/a'}
          </Descriptions.Item>
          <Descriptions.Item key='Fleet type' label='Fleet type'>
            {course?.fleet_type ?? 'n/a'}
          </Descriptions.Item>
          <Descriptions.Item key='Number of users' label='Number of users'>
            {course?.number_of_users ?? 'n/a'}
          </Descriptions.Item>
          <Descriptions.Item key='Start date' label='Start date'>
            <LocalDateTime
              date={course?.start_at}
              timezone={course?.timezone}
            />
          </Descriptions.Item>
          <Descriptions.Item key='End date' label='End date'>
            <LocalDateTime date={course?.end_at} timezone={course?.timezone} />
          </Descriptions.Item>
          <Descriptions.Item key='Users' label='Users'>
            {users}
          </Descriptions.Item>
          <Descriptions.Item key='Instructors' label='Instructors'>
            {instructors}
          </Descriptions.Item>
          <Descriptions.Item key='CAS supports' label='CAS supports'>
            {casSupports}
          </Descriptions.Item>
          <Descriptions.Item key='Case-uploading' label='Case-uploading'>
            {caseUploading}
          </Descriptions.Item>
          <Descriptions.Item key='On-demand' label='On-demand'>
            <Switch
              checked={course?.on_demand}
              disabled={updatingCourseSetting}
              loading={
                updatingCourseSetting && courseSetting?.field === 'on_demand'
              }
              onChange={(checked) =>
                updateCourseSetting({ field: 'on_demand', checked })
              }
            />
          </Descriptions.Item>
          <Descriptions.Item key='Load balancing' label='Load balancing'>
            <Switch
              checked={course?.is_auto_allocate_server}
              disabled={updatingCourseSetting}
              loading={
                updatingCourseSetting &&
                courseSetting?.field === 'is_auto_allocate_server'
              }
              onChange={(checked) =>
                updateCourseSetting({ field: 'is_auto_allocate_server', checked })
              }
            />
          </Descriptions.Item>
          <Descriptions.Item
            key='Daily syncing servers'
            label='Daily syncing servers'
          >
            <Switch
              checked={course?.sync_servers_enabled}
              disabled={updatingCourseSetting}
              loading={
                updatingCourseSetting &&
                courseSetting?.field === 'sync_servers_enabled'
              }
              onChange={(checked) =>
                updateCourseSetting({ field: 'sync_servers_enabled', checked })
              }
            />
          </Descriptions.Item>
          <Descriptions.Item
            key='Number of users per server'
            label='Number of users per server'
          >
            {course?.users_per_server}
          </Descriptions.Item>
          <Descriptions.Item
            key='Notification receivers'
            label='Notification receivers'
          >
            {course?.notification_receivers ?? 'n/a'}
          </Descriptions.Item>
          <Descriptions.Item key='Meeting link' label='Meeting link'>
            {course?.meeting_link ?? 'n/a'}
          </Descriptions.Item>
          <Descriptions.Item key='Meeting passcode' label='Meeting passcode'>
            {course?.meeting_passcode ?? 'n/a'}
          </Descriptions.Item>
          <Descriptions.Item
            key='Close hands-on registration at'
            label='Close hands-on registration '
          >
            {course?.max_number_of_participants ?? 'n/a'}
          </Descriptions.Item>
          <Descriptions.Item key='Price' label={`Price (${course?.currency ?? 'USD'})`}>
            {course?.registration_fee_formatted}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Card>
        <Tabs
          activeKey={
            items.some((item) => item.key === queryValue.at(0))
              ? queryValue.at(0)
              : items[0].key
          }
          items={items}
          onChange={(key) => setQueryValue(key)}
        />
      </Card>

      <Modal
        title={'Assign a user'}
        open={openAssignUserModel}
        destroyOnHidden
        onOk={() => {
          assignUserForm.current.submit()
        }}
        onCancel={() => setOpenAssignUserModel(false)}
      >
        <Form ref={assignUserForm} preserve={false} onFinish={handleAssignUser}>
          <Form.Item label='User' name='user_id'>
            <Select
              showSearch
              optionFilterProp='label'
              options={data?.map((item) => ({
                value: item.id,
                label: item.name
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default CourseDetails
